import { useEffect, useState } from "react"

const STORAGE_KEY = "jawnaspoldzielnia.theme.mode"
const MODES = ["system", "light", "dark"]
const ICONS = {
  system: {
    path: "M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25m18 0A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25m18 0V12a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 12V5.25",
  },
  light: {
    path: "M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z",
  },
  dark: {
    path: "M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z",
  },
}

export default function ThemeSwitcher({ labels, placement = "floating", variant = "modes" }) {
  const modeLabels = labels.modes || {}
  const [mode, setMode] = useState(() => readStoredMode())
  const systemTheme = useSystemTheme()
  const classes = ["jawnaspoldzielnia-theme-switcher", `jawnaspoldzielnia-theme-switcher--${placement}`].join(" ")
  const resolvedTheme = resolveTheme(mode, systemTheme)

  useEffect(() => {
    applyTheme(mode, systemTheme)

    try {
      window.localStorage.setItem(STORAGE_KEY, mode)
    } catch (_error) {
      return
    }
  }, [mode, systemTheme])

  if (variant === "icon-toggle") {
    const isDark = resolvedTheme === "dark"
    const nextMode = isDark ? "light" : "dark"

    return (
      <div className={classes} aria-label={labels.label} data-theme-switcher-variant={variant}>
        <button
          type="button"
          className="jawnaspoldzielnia-theme-switcher__option"
          data-theme-switcher-mode-param={nextMode}
          aria-label={modeLabels[nextMode] || labels.label}
          aria-pressed={isDark}
          onClick={() => setMode(nextMode)}
        >
          <ThemeIcon mode={nextMode} />
        </button>
      </div>
    )
  }

  return (
    <div className={classes} aria-label={labels.label}>
      {MODES.map((candidate) => (
        <button
          key={candidate}
          type="button"
          className="jawnaspoldzielnia-theme-switcher__option"
          data-theme-switcher-mode-param={candidate}
          aria-label={modeLabels[candidate]}
          aria-pressed={mode === candidate}
          onClick={() => setMode(candidate)}
        >
          <ThemeIcon mode={candidate} />
        </button>
      ))}
    </div>
  )
}

function ThemeIcon({ mode }) {
  const icon = ICONS[mode] || ICONS.system

  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      data-theme-switcher-icon={mode}
      fill="none"
      focusable="false"
      stroke="currentColor"
      strokeWidth="1.8"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d={icon.path} />
    </svg>
  )
}

function readStoredMode() {
  if (typeof window === "undefined") return "system"

  try {
    const storedMode = window.localStorage.getItem(STORAGE_KEY)
    return MODES.includes(storedMode) ? storedMode : "system"
  } catch (_error) {
    return "system"
  }
}

function useSystemTheme() {
  const [systemTheme, setSystemTheme] = useState(() => resolveSystemTheme())

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)")
    const update = () => setSystemTheme(resolveSystemTheme())

    media.addEventListener("change", update)

    return () => media.removeEventListener("change", update)
  }, [])

  return systemTheme
}

function resolveSystemTheme() {
  if (typeof window === "undefined") return "light"

  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

function resolveTheme(mode, systemTheme) {
  return mode === "system" ? systemTheme : mode
}

function applyTheme(mode, systemTheme) {
  const theme = resolveTheme(mode, systemTheme)
  const root = document.documentElement

  root.dataset.themeMode = mode
  root.dataset.theme = theme
  root.style.colorScheme = theme
}
